import { useState } from 'react';
import { X, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useIntegrationLogs } from '../../hooks/useIntegrations';

interface SyncLogsProps {
  integrationId: number;
  integrationName: string;
  onClose: () => void;
}

export function IntegrationSyncLogs({ integrationId, integrationName, onClose }: SyncLogsProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useIntegrationLogs(integrationId, page);

  const logs = data?.logs || [];
  const totalPages = data?.totalPages || 1;

  function statusIcon(status: string) {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'ERROR':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'RUNNING':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return null;
    }
  }

  function formatDate(date: string) {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatDuration(ms: number | null) {
    if (!ms) {
      return '-';
    }
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(1)}s`;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-secondary-200">
          <h2 className="text-lg font-semibold text-secondary-900">
            Histórico — {integrationName}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary-100">
            <X className="w-5 h-5 text-secondary-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-secondary-500 py-8">Nenhuma sincronização realizada</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-secondary-500 border-b border-secondary-200">
                  <th className="pb-2 font-medium">Data/Hora</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium text-center">Criados</th>
                  <th className="pb-2 font-medium text-center">Atualizados</th>
                  <th className="pb-2 font-medium text-center">Convertidos</th>
                  <th className="pb-2 font-medium text-right">Duração</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log: any) => (
                  <tr key={log.id} className="border-b border-secondary-100">
                    <td className="py-2.5">{formatDate(log.createdAt)}</td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-1.5">
                        {statusIcon(log.status)}
                        <span
                          className={
                            log.status === 'ERROR'
                              ? 'text-red-600'
                              : log.status === 'SUCCESS'
                                ? 'text-green-600'
                                : 'text-blue-600'
                          }
                        >
                          {log.status}
                        </span>
                      </div>
                      {log.errorMessage && (
                        <p className="text-xs text-red-500 mt-0.5">{log.errorMessage}</p>
                      )}
                    </td>
                    <td className="py-2.5 text-center">{log.leadsCreated}</td>
                    <td className="py-2.5 text-center">{log.leadsUpdated}</td>
                    <td className="py-2.5 text-center">{log.leadsConverted}</td>
                    <td className="py-2.5 text-right text-secondary-500">
                      {formatDuration(log.durationMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="border-t border-secondary-200 p-4 flex justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm rounded-lg border border-secondary-300 hover:bg-secondary-50 disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="px-3 py-1.5 text-sm text-secondary-600">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border border-secondary-300 hover:bg-secondary-50 disabled:opacity-50"
            >
              Próximo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
